import React from "react";

import { ArrowRightOutlined, PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";

import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import CoverageSummarySection from "./GlobalTestSpec/CoverageSummarySection";
import TestSpecStatusChip from "./GlobalTestSpec/TestSpecStatusChip";
import {
  filterSpecManagerProductsBySearch,
  formatMrlCountriesListSubtitleFromEditorShape,
  formatPreviewTestSpec,
  formatPreviewTestType,
  isCustomSpecProduct,
} from "./Helper";
import ProductList from "./ProductList";

import { ReactComponent as LoadingDots } from "../../../../../assets/images/common/LoadingDots.svg";
import { ReactComponent as ErrorWarningFillIcon } from "../../../../../assets/images/product/specmanager/error-warning-fill.svg";
import { ReactComponent as MicroScopeIcon } from "../../../../../assets/images/product/specmanager/microscope-line.svg";

function EditSpecTestSpecPreviewDetailsBox({ productTitle, dataOfSpecEditing }) {
  const spec = dataOfSpecEditing?.[0] ?? {};

  const isMRL = Array.isArray(spec?.countries_lst) && spec.countries_lst.length > 0;
  const testTypeValue = isMRL ? "MRL" : formatPreviewTestType(spec);
  const testSpecValueLabel = isMRL
    ? formatMrlCountriesListSubtitleFromEditorShape(spec.countries_lst)
    : formatPreviewTestSpec(spec);

  return (
    <div className="EditSpec__TestSpecPreviewDetailsBox">
      <div className="EditSpec__TestSpecPreviewTitleRow">
        <div className="EditSpec__TestSpecPreviewRightCol">
          <div className="EditSpec__TestSpecPreviewTestName" title={productTitle}>
            {productTitle}
          </div>

          <div
            className="EditSpec__TestSpecPreviewDetailsLine"
            title={isMRL ? `${testTypeValue} • ${testSpecValueLabel}` : undefined}
          >
            <span className="EditSpec__TestSpecPreviewStrongLabel">
              Test Type:
              {" "}
            </span>
            {" "}
            <span className="EditSpec__TestSpecPreviewDetailsValue">{testTypeValue}</span>
            <span className="EditSpec__TestSpecPreviewBullet">•</span>
            <span className="EditSpec__TestSpecPreviewStrongLabel">
              Test Spec:
              {" "}
            </span>
            {" "}
            <span className="EditSpec__TestSpecPreviewDetailsValue">{testSpecValueLabel}</span>
          </div>
        </div>

        <TestSpecStatusChip status="added" ariaLabel="Spec Added" />
      </div>
    </div>
  );
}

function EditSpecTestSpecPreviewProductsSection({
  testSpecPreviewSelectedProducts,
  testSpecPreviewFields,
  testSpecPreviewLinkReportJsonFields,
  testSpecSearchValue,
}) {
  const previewSelectedFiltered = filterSpecManagerProductsBySearch(
    testSpecPreviewSelectedProducts,
    testSpecSearchValue,
    testSpecPreviewLinkReportJsonFields,
  );

  return (
    <div className="EditSpec__TestSpecPreviewProductsArea">
      <h3 className="EditSpec__TestSpecPreviewProductsTitle">
        {`Selected Products (${testSpecPreviewSelectedProducts.length})`}
      </h3>
      <ProductList
        selectedProducts={previewSelectedFiltered}
        totalSelectedCount={testSpecPreviewSelectedProducts.length}
        searchTerm={testSpecSearchValue}
        loading={false}
        fields={testSpecPreviewFields}
        linkReportJsonFields={testSpecPreviewLinkReportJsonFields}
        tooltipPlacement="top"
      />
    </div>
  );
}

/**
 * Body, step footer, and bottom action strip for global Test Spec editing inside EditSpec.
 */
export default function EditSpecTestSpecWorkflow({
  loading,
  editing,
  dataOfSpecEditing,
  testSpecPreviewSelectedProducts,
  testSpecPreviewFields,
  testSpecPreviewLinkReportJsonFields,
  testSpecSearchValue,
  productSectionWithFooter,
  productTitle,
  renderTable,
  previewPrimaryDisabled,
  previewSelectionTooltip,
  previewFooterLoading,
  testSpecAddCountFromAllList,
  testSpecStandardFollowCount,
  onAdvancePreviewStep,
  onBackFooter,
  canAddSpec,
  renderButtons,
}) {
  return (
    <>
      <div className="EditSpec__TestSpecBody">
        {loading && (
          <div className="EditSpec__TestSpecLoading">
            <LoadingDots width={28} height={28} fill="#19305A" />
          </div>
        )}

        {!loading && editing && (
          <>
            <div className="EditSpec__TestSpecLeftPanel">
              <div className="EditSpec__TestSpecTableArea">
                <div className="EditSpec__TestSpecEditorContainer">
                  {renderTable()}
                </div>
              </div>

              <div className="EditSpec__TestSpecCoverageArea">
                <CoverageSummarySection
                  products={testSpecPreviewSelectedProducts}
                />
              </div>

              {testSpecPreviewSelectedProducts.some(isCustomSpecProduct) && (
                <div className="EditSpec__TestSpecCustomSpecAlert" role="status">
                  <ErrorWarningFillIcon className="EditSpec__TestSpecCustomSpecAlertIcon" aria-hidden />
                  <div className="EditSpec__TestSpecCustomSpecAlertCopy">
                    <div className="EditSpec__TestSpecCustomSpecAlertTitle">
                      Changes apply to all products using this standard.
                    </div>
                    <div className="EditSpec__TestSpecCustomSpecAlertText">
                      Products with custom (product-specific) limits will keep their individual values.
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="EditSpec__TestSpecRightPanel">
              {productSectionWithFooter && (
                <div className="EditSpec__ProductSection">
                  {productSectionWithFooter}
                </div>
              )}
            </div>
          </>
        )}

        {!loading && !editing && (
          <div className="EditSpec__TestSpecPreviewLayout">
            <EditSpecTestSpecPreviewDetailsBox
              productTitle={productTitle}
              dataOfSpecEditing={dataOfSpecEditing}
            />
            <EditSpecTestSpecPreviewProductsSection
              testSpecPreviewSelectedProducts={testSpecPreviewSelectedProducts}
              testSpecPreviewFields={testSpecPreviewFields}
              testSpecPreviewLinkReportJsonFields={testSpecPreviewLinkReportJsonFields}
              testSpecSearchValue={testSpecSearchValue}
            />
          </div>
        )}
      </div>
      {editing && !loading && (
        <footer className="UpdateProductList__Footer EditSpec__TestSpecModalFooter">
          <div className="UpdateProductList__FooterLeft">
            <div className="TestSpecOverview__FooterFollowsBadge">
              <MicroScopeIcon className="TestSpecOverview__FooterBadgeIcon" />
              <strong>
                {testSpecStandardFollowCount}
                {" "}
                Products
              </strong>
              {" "}
              follow the standard spec
            </div>
          </div>
          <div className="UpdateProductList__FooterRight">
            <Button
              type="default"
              className="UpdateProductList__FooterCancel"
              onClick={onBackFooter}
            >
              Cancel
            </Button>
            <StyledTooltip
              title={previewPrimaryDisabled && !previewFooterLoading ? previewSelectionTooltip : ""}
              placement="top"
              getPopupContainer={(trigger) => trigger?.closest(".ant-drawer-body") || trigger?.closest(".ant-modal-body") || document.body}
            >
              <Button
                type="primary"
                className="UpdateProductList__FooterPrimary"
                disabled={previewPrimaryDisabled}
                onClick={() => onAdvancePreviewStep()}
                loading={previewFooterLoading}
                icon={testSpecAddCountFromAllList > 0 ? <PlusOutlined /> : <ArrowRightOutlined />}
                iconPosition="end"
              >
                {testSpecAddCountFromAllList > 0 ? (
                  <span>
                    Add Products (
                    {testSpecAddCountFromAllList}
                    )
                  </span>
                ) : (
                  <span>
                    Preview Selection
                  </span>
                )}
              </Button>
            </StyledTooltip>
          </div>
        </footer>
      )}
      {!editing && (
        <div
          id="productReportEditSpecButtons"
          className="productReportEditSpecButtonsSection EditSpec__TestSpecButtons"
        >
          {canAddSpec ? renderButtons() : null}
        </div>
      )}
    </>
  );
}
